import { useEffect, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import axios from 'axios';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useDispatch } from 'react-redux';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { register } from '../../../../components/charts/charts-data-config';
import { AuthHeader } from '../../../../utils/helpers/auth-headers';
import { AlertSeverity } from '../../../../utils/enum/Alerts';
import './HistoryStatistics.scss';
import { setAlertData } from '../../../../stores/alertStore';
import ChartVerticalBar from '../../../../components/charts/ChartsVerticalBar';
import ChartVerticalBarStacked from '../../../../components/charts/ChartsVerticalStackedBar';

export default function HistoryMonthlyStatistics() {
  ChartJS.register(register);

  const { t } = useTranslation();
  const paramsRoute = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const i18nLang = i18next.language;

  const currentDate = new Date();
  const [dateFrom, setDateFrom] = useState<Date | null>(new Date(currentDate.getFullYear() - 4, currentDate.getMonth(), 1));
  const [dateTo, setDateTo] = useState<Date | null>(new Date(currentDate.getFullYear(), currentDate.getMonth(), 30));
  const [locale, setLocale] = useState('fr');

  const [chartMeetingDataMonthly, setChartMeetingDataMonthly] = useState({});
  const [chartParticipantDataMonthly, setChartParticipantDataMonthly] = useState({});
  const [chartConcurrentMeetingDataMonthly, setChartConcurrentMeetingDataMonthly] = useState({});
  const [chartConcurrentParticipantDataMonthly, setChartConcurrentParticipantDataMonthly] = useState({});
  const [chartBrowserDataMonthly, setChartBrowserDataMonthly] = useState({});
  const [chartBrowserVersionDataMonthly, setChartBrowserVersionDataMonthly] = useState({});
  const [chartConcurrentAudioDataMonthly, setChartConcurrentAudioDataMonthly] = useState({});
  const [chartConcurrentPortDataMonthly, setChartConcurrentPortDataMonthly] = useState({});
  const [chartProtocolsDataMonthly, setChartProtocolsDataMonthly] = useState({});

  const [instanceStatsMonthly, setInstanceStatsMonthly] = useState<any>([]);

  const setChartsMonthly = () => {
    const labelsMonthly: string[] = [];
    const meetingData: number[] = [];
    const participantData: number[] = [];
    const maxConcurrentMeetingData: number[] = [];
    const maxConcurrentParticipantData: number[] = [];
    const browsersData: any[] = [];
    const browserVersionsData: any[] = [];
    const maxConcurrentAudioData: number[] = [];
    const maxConcurrentPortData: number[] = [];
    const protocolsData: any[] = [];
    instanceStatsMonthly.forEach((value: any, index: number) => {
      labelsMonthly.push(new Date(value.startTime).toLocaleDateString());
      meetingData.push(value.meetingCount);
      participantData.push(value.participantCount);
      maxConcurrentMeetingData.push(value.maxConcurrentMeeting);
      maxConcurrentParticipantData.push(value.maxConcurrentParticipant);
      browsersData.push(value.browser);
      browserVersionsData.push(value.browserVersion);
      maxConcurrentAudioData.push(value.maxConcurrentAudio);
      maxConcurrentPortData.push(value.maxConcurrentPort);
      protocolsData.push(value.protocol);
    });
    setChartMeetingDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.meeting'),
      data: meetingData,
      label: t('HistoryStatistics.chart.meetingLabel'),
      labels: labelsMonthly,
    });
    setChartParticipantDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.participant'),
      data: participantData,
      label: t('HistoryStatistics.chart.participantLabel'),
      labels: labelsMonthly,
    });
    setChartConcurrentMeetingDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.concurrentMeeting'),
      data: maxConcurrentMeetingData,
      label: t('HistoryStatistics.chart.concurrentMeetingLabel'),
      labels: labelsMonthly,
    });
    setChartConcurrentParticipantDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.concurrentParticipant'),
      data: maxConcurrentParticipantData,
      label: t('HistoryStatistics.chart.concurrentParticipantLabel'),
      labels: labelsMonthly,
    });
    const browsersLabelSet: string[] = [];
    browsersData.forEach((value: any, index: number) => {
      if (Object.entries(value).length !== 0) {
        Object.entries(value).map(([key, valueLabel]) => browsersLabelSet.push(key));
      }
    });
    const defBrowsersLabelSet = Array.from(new Set(browsersLabelSet));
    const browsersDataSets: any[] = [];
    defBrowsersLabelSet.forEach((value: any, index: number) => {
      browsersDataSets[index] = {
        label: value,
        data: [],
        backgroundColor: `rgb(${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)})`
      };
      browsersData.forEach((valueBrowser: any, indexBrowser: number) => {
        if (Object.keys(valueBrowser).includes(value)) {
          browsersDataSets[index].data.push(valueBrowser[value]);
        } else {
          browsersDataSets[index].data.push(0);
        }
      });
    });
    setChartBrowserDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.clientPerBrowser'),
      dataSets: browsersDataSets,
      labels: labelsMonthly,
    });
    const browserVersionsLabelSet: string[] = [];
    browserVersionsData.forEach((value: any, index: number) => {
      if (Object.entries(value).length !== 0) {
        Object.entries(value).map(([key, valueLabel]) => browserVersionsLabelSet.push(key));
      }
    });
    const defBrowserVersionsLabelSet = Array.from(new Set(browserVersionsLabelSet));
    const browserVersionsDataSets: any[] = [];
    defBrowserVersionsLabelSet.forEach((value: any, index: number) => {
      browserVersionsDataSets[index] = {
        label: value,
        data: [],
        backgroundColor: `rgb(${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)})`
      };
      browserVersionsData.forEach((valueBrowser: any, indexBrowser: number) => {
        if (Object.keys(valueBrowser).includes(value)) {
          browserVersionsDataSets[index].data.push(valueBrowser[value]);
        } else {
          browserVersionsDataSets[index].data.push(0);
        }
      });
    });
    setChartBrowserVersionDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.versionPerBrowser'),
      dataSets: browserVersionsDataSets,
      labels: labelsMonthly,
    });
    setChartConcurrentAudioDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.concurrentAudio'),
      data: maxConcurrentAudioData,
      label: t('HistoryStatistics.chart.concurrentAudioLabel'),
      labels: labelsMonthly,
    });
    setChartConcurrentPortDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.concurrentPort'),
      data: maxConcurrentPortData,
      label: t('HistoryStatistics.chart.concurrentPortLabel'),
      labels: labelsMonthly,
    });
    const protocolsLabelSet: string[] = [];
    protocolsData.forEach((value: any, index: number) => {
      if (Object.entries(value).length !== 0) {
        Object.entries(value).map(([key, valueLabel]) => protocolsLabelSet.push(key));
      }
    });
    const defProtocolsLabelSet = Array.from(new Set(protocolsLabelSet));
    const protocolsDataSets: any[] = [];
    defProtocolsLabelSet.forEach((value: any, index: number) => {
      protocolsDataSets[index] = {
        label: value,
        data: [],
        backgroundColor: `rgb(${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)})`
      };
      protocolsData.forEach((valueBrowser: any, indexBrowser: number) => {
        if (Object.keys(valueBrowser).includes(value)) {
          protocolsDataSets[index].data.push(valueBrowser[value]);
        } else {
          protocolsDataSets[index].data.push(0);
        }
      });
    });
    setChartProtocolsDataMonthly({
      displaySubtitle: false,
      displayTitle: true,
      title: t('HistoryStatistics.chartTitle.protocols'),
      dataSets: protocolsDataSets,
      labels: labelsMonthly,
    });
  };

  const getStatisticsMonthly = () => {
    const startFrom = moment(dateFrom).utc().startOf('month');
    const endTo = moment(dateTo).utc().endOf('month');
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/historyStatMonthly?startTime(gte)=${startFrom?.toISOString()}&endTime(lte)=${endTo?.toISOString()}`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setInstanceStatsMonthly(response.data.data.sort((a: any, b: any) => new Date(a.startTime).valueOf() - new Date(b.startTime).valueOf())))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  useEffect(() => {
    getStatisticsMonthly();
  }, [navigate, dateFrom, dateTo]);

  useEffect(() => {
    setChartsMonthly();
  }, [instanceStatsMonthly, i18nLang]);

  useEffect(() => {
    if (i18nLang === 'en') {
      setLocale('en');
      moment.updateLocale('en', { week: { dow: 1 } });
    } else {
      setLocale('fr');
      moment.updateLocale('fr', { week: { dow: 1 } });
    }
  }, [i18nLang]);

  const handleDateFrom = (newValue: Date) => {
    if (newValue && dateTo && newValue > dateTo) {
      setDateFrom(new Date(dateTo));
    } else {
      setDateFrom(new Date(newValue));
    }
  };

  const handleDateTo = (newValue: Date) => {
    if (newValue && dateFrom && newValue < dateFrom) {
      setDateTo(new Date(dateFrom));
    } else {
      setDateTo(new Date(newValue));
    }
  };

  return (
    <div>
      <div className="category-container date-picker">
        <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
          <DatePicker
            label={t('HistoryList.from')}
            value={dateFrom}
            onChange={(newValue) => {
              handleDateFrom(newValue as Date);
            }}
            renderInput={(params) => <TextField {...params} />}
            views={['month', 'year']}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
          <DatePicker
            label={t('HistoryList.to')}
            value={dateTo}
            onChange={(newValue) => {
              handleDateTo(newValue as Date);
            }}
            renderInput={(params) => <TextField {...params} />}
            views={['month', 'year']}
          />
        </LocalizationProvider>
      </div>
      <div className="history-stats">
        <div className="charts">
          <ChartVerticalBar chartData={chartMeetingDataMonthly} />
          <ChartVerticalBar chartData={chartParticipantDataMonthly} />
          <ChartVerticalBar chartData={chartConcurrentMeetingDataMonthly} />
          <ChartVerticalBar chartData={chartConcurrentParticipantDataMonthly} />
          <ChartVerticalBarStacked chartData={chartBrowserDataMonthly} legendDisplay={false} />
          <ChartVerticalBarStacked chartData={chartBrowserVersionDataMonthly} legendDisplay={false} />
          <ChartVerticalBar chartData={chartConcurrentAudioDataMonthly} />
          <ChartVerticalBar chartData={chartConcurrentPortDataMonthly} />
          <ChartVerticalBarStacked chartData={chartProtocolsDataMonthly} />
        </div>
      </div>
    </div>
  );
}
